import { useFileSignedUrlFetcher } from 'features/rolodex'
import { useCallback } from 'react'
import { ReportESignatureRecord } from 'types'

import { sha256Hex } from './utils/sha256Hex'

/** Reads report files from Rolodex, with the hash check every signed PDF must pass before use. */
export function useSignedPdfFetcher() {
  const { fetchSignedUrlFromRolodexOnly } = useFileSignedUrlFetcher()

  const fetchFileBytes = useCallback(
    async (metadataEntryId: string, path: string, signal?: AbortSignal) => {
      const signed = await fetchSignedUrlFromRolodexOnly({
        queryOrQueries: { entry_id: metadataEntryId, path },
      })
      if (!signed?.url) throw new Error(`Could not locate ${path}`)
      signal?.throwIfAborted()
      const response = await fetch(signed.url, { signal })
      if (!response.ok) throw new Error(`Failed to download ${path}`)
      return response.arrayBuffer()
    },
    [fetchSignedUrlFromRolodexOnly]
  )

  const fetchVerifiedSignedPdf = useCallback(
    async (metadataEntryId: string, record: ReportESignatureRecord) => {
      const bytes = await fetchFileBytes(
        metadataEntryId,
        record.signedDocumentPath
      )
      if ((await sha256Hex(bytes)) !== record.signedDocumentSha256) {
        throw new Error(
          'The signed PDF does not match its recorded fingerprint and cannot be trusted.'
        )
      }
      return bytes
    },
    [fetchFileBytes]
  )

  return { fetchFileBytes, fetchVerifiedSignedPdf }
}
